import { axiosInstance } from './api';

// Backend wired in `OpsTaskViewSet` (services/plugins/ops_tasks/api/views.py).
// Returns DRF-paginated `{count, next, previous, results: [...]}` — we read
// only `results`. The legacy server-rendered HTML at /ops_tasks/dashboard/
// remains untouched and continues to serve workers via Telegram WebApp.

export type OpsTaskStatus = 'pending' | 'overdue' | 'done' | 'pre_deadline' | 'created';

export interface OpsTaskReportPhoto {
  id: number;
  image_url: string | null;
  created_at: string;
}

export interface OpsTaskReportVideo {
  id: number;
  video_url: string | null;
  created_at: string;
}

export interface OpsTaskReport {
  id: number;
  reporter_username: string;
  text: string;
  created_at: string;
  closed_task: boolean;
  photos: OpsTaskReportPhoto[];
  videos: OpsTaskReportVideo[];
}

export interface OpsTaskListItem {
  id: number | string;
  title: string;
  status: OpsTaskStatus;
  due_at: string | null;     // ISO timestamp
  done_at?: string | null;
  created_at?: string;
  zone: number | string;
  answer_format?: string;
  description?: string | null;
  latest_report?: OpsTaskReport | null;
}

export interface ListOpsTasksResponse {
  results: OpsTaskListItem[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface ListOpsTasksParams {
  zoneId: string;
  /**
   * Accepted for API symmetry with the legacy frontend; currently a no-op
   * since OpsTask doesn't track per-user assignment server-side. Once
   * ZoneMembership-aware filtering lands the backend will honor `?only_mine=1`.
   */
  onlyMine?: boolean;
  /** YYYY-MM-DD in the zone's local timezone. Mirrors the legacy /dashboard/ ?date= param. */
  date?: string;
  onlyToday?: boolean;
  status?: OpsTaskStatus;
  /** Drill into one ChecklistItem. Server flips ordering to newest-first. */
  checklistItemId?: number | string;
  /** Drill into multiple ChecklistItems (weekday-variants of one task,
   *  merged into a single analytics digest row). Sent as a CSV. */
  checklistItemIds?: (number | string)[];
  /** YYYY-MM-DD inclusive lower bound on due_at (analytics drill-in scope). */
  from?: string;
  /** YYYY-MM-DD inclusive upper bound on due_at (analytics drill-in scope). */
  to?: string;
  /** Override DRF default page size (10) — used by analytics drill-in to
   *  pull every execution in one go instead of paginating manually. */
  pageSize?: number;
}

export const listOpsTasks = ({
  zoneId,
  onlyMine,
  onlyToday = false,
  date,
  status,
  checklistItemId,
  checklistItemIds,
  from,
  to,
  pageSize,
}: ListOpsTasksParams) => {
  const checklistParam = checklistItemIds?.length
    ? checklistItemIds.join(',')
    : checklistItemId != null
      ? String(checklistItemId)
      : undefined;
  return axiosInstance.get<ListOpsTasksResponse>('/ops_tasks/api/tasks/', {
    params: {
      zone_id: zoneId,
      ...(date ? { date } : onlyToday ? { only_today: 1 } : {}),
      ...(onlyMine ? { only_mine: 1 } : {}),
      ...(status ? { status } : {}),
      ...(checklistParam != null ? { checklist_item: checklistParam } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(pageSize != null ? { page_size: pageSize } : {}),
    },
  });
};

export interface SubmitReportInput {
  text?: string;
  photos?: File[];
  videos?: File[];
  close?: boolean;
}

export const submitOpsTaskReport = (taskId: number | string, input: SubmitReportInput) => {
  const fd = new FormData();
  if (input.text) fd.append('text', input.text);
  fd.append('close', input.close === false ? '0' : '1');
  for (const p of input.photos ?? []) fd.append('photos', p);
  for (const v of input.videos ?? []) fd.append('videos', v);
  return axiosInstance.post<OpsTaskReport>(`/ops_tasks/api/tasks/${taskId}/report/`, fd);
};

// exchangeTelegramToken moved to src/api/auth.ts (FE-33)
